import type { ContactRecipientTuple, RecipientTuple } from '@liscio/api';

import type { SelectedContact } from 'components/AccountContactSelector/AccountContactSelector';


/**
 * The shape `AccountContactSelectorField` writes to its form field: one entry
 * per selected contact, with the contact and its account.
 */
export interface SelectedParticipant {
  recipient: SelectedContact;
  account: { account_id: number; account_name: string } | null;
  is_recipient?: boolean;
  canRemove?: boolean;
}

export interface PreselectedRecipient {
  contact: {
    id: number;
    uuid: string;
    cpaUserId: number;
    firstName: string | null;
    lastName: string;
    email: string | null;
  };
  account: { id: number; name: string };
}

/**
 * Builds the form's initial `participants` value from an already-created
 * contact + account, in the same `SelectedParticipant` shape the selector emits
 * on change (so the value is consistent whether or not the user edits it).
 */
export function buildPreselectedParticipants(
  preselected: PreselectedRecipient
): SelectedParticipant[] {
  const { contact, account } = preselected;
  return [
    {
      recipient: {
        id: contact.id,
        uuid: contact.uuid,
        cpa_user_id: contact.cpaUserId,
        first_name: contact.firstName ?? '',
        last_name: contact.lastName,
        email: contact.email ?? '',
        type: 'contact',
        account_id: account.id,
        account_name: account.name,
      },
      account: { account_id: account.id, account_name: account.name },
      is_recipient: true,
    },
  ];
}

/**
 * The selector's `id` is the cpa_user_id when the contact has one (the message
 * forms key on it), so the contact id must come from `contact_id` first. The V3
 * share endpoint resolves `recipients[n][0]` as `contacts.id`.
 */
function resolveParticipantIds(participant: SelectedParticipant) {
  return {
    contactId: Number(
      participant.recipient?.contact_id ?? participant.recipient?.id
    ),
    accountId: Number(
      participant.account?.account_id ??
        participant.recipient?.account_id ??
        participant.recipient?.selectedAccountId
    ),
  };
}

/**
 * Converts the selector's `participants` value into the `recipients` tuples the
 * share API expects — `[contactId, accountId, 'user']` — mirroring the existing
 * `mungeToRecipientTuples` used by the normal new-file share. Entries missing a
 * contact or account id are dropped.
 */
export function participantsToRecipientTuples(
  participants: SelectedParticipant[] | undefined
): RecipientTuple[] {
  return (participants ?? [])
    .map((participant): RecipientTuple | null => {
      const { contactId, accountId } = resolveParticipantIds(participant);
      if (!contactId || !accountId) {
        return null;
      }
      return [contactId, accountId, 'user'];
    })
    .filter((tuple): tuple is RecipientTuple => tuple !== null);
}

/**
 * Like {@link participantsToRecipientTuples}, but keeps contacts that have no
 * account, sending `[contactId, '', 'user']` as legacy upload does. Only entries
 * missing a contact id are dropped.
 */
export function participantsToContactRecipientTuples(
  participants: SelectedParticipant[] | undefined
): ContactRecipientTuple[] {
  return (participants ?? [])
    .map((participant): ContactRecipientTuple | null => {
      const { contactId, accountId } = resolveParticipantIds(participant);
      if (!contactId) {
        return null;
      }
      return [contactId, accountId || '', 'user'];
    })
    .filter((tuple): tuple is ContactRecipientTuple => tuple !== null);
}
